import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import Swal from 'sweetalert2';
import './Cotizador.css';
import CotizadorPreview from './CotizadorPreview';
import { generatePdf } from './cotizadorPdf';
import { getNextNumber, savePresupuesto } from './cotizadorStorage';
import logo from '../../assets/logoN.png';

const TIPOS_SERVICIO = ['Viaje', 'Turismo', 'Evento', 'Escolar', 'Deportivo', 'Otro'];

function formatMoney(value) {
  const num = parseFloat(value) || 0;
  return num.toLocaleString('es-AR', {
    style: 'currency',
    currency: 'ARS',
    minimumFractionDigits: 2,
  });
}

export default function Cotizador() {
  const previewRef = useRef(null);
  const [loadingPdf, setLoadingPdf] = useState(false);

  const today = new Date().toISOString().split('T')[0];
  const nextNum = getNextNumber();

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      numPresupuesto: nextNum,
      fecha: today,
      validezPresupuesto: '30 días desde la fecha de emisión',
      tipoServicio: 'Viaje',
      precioBase: '',
      adicionales: '',
      descuento: '',
    },
  });

  const values = watch();
  const base = parseFloat(values.precioBase) || 0;
  const adic = parseFloat(values.adicionales) || 0;
  const desc = parseFloat(values.descuento) || 0;
  const total = base + adic - desc;

  const handleDownloadPdf = async () => {
    setLoadingPdf(true);
    try {
      await generatePdf(previewRef, values.numPresupuesto || 'SP');
    } catch {
      Swal.fire({
        icon: 'error',
        title: 'Error al generar el PDF',
        text: 'Intentalo nuevamente.',
        confirmButtonColor: 'rgb(133, 15, 15)',
      });
    } finally {
      setLoadingPdf(false);
    }
  };

  const handleSave = handleSubmit((data) => {
    savePresupuesto({ ...data, total });
    Swal.fire({
      icon: 'success',
      title: 'Presupuesto guardado',
      text: `El presupuesto N° ${data.numPresupuesto} fue guardado en este navegador.`,
      confirmButtonColor: 'rgb(133, 15, 15)',
    });
  });

  const handleClear = () => {
    Swal.fire({
      title: '¿Limpiar formulario?',
      text: 'Se borrará todo lo cargado.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: 'rgb(133, 15, 15)',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Sí, limpiar',
      cancelButtonText: 'Cancelar',
    }).then((result) => {
      if (result.isConfirmed) {
        reset({
          numPresupuesto: getNextNumber(),
          fecha: new Date().toISOString().split('T')[0],
          validezPresupuesto: '30 días desde la fecha de emisión',
          tipoServicio: 'Viaje',
          precioBase: '',
          adicionales: '',
          descuento: '',
        });
      }
    });
  };

  return (
    <div className="cotizador-page">

      {/* TOPBAR */}
      <div className="cotizador-topbar">
        <div className="cotizador-topbar-brand">
          <img src={logo} alt="Transportes Leo" />
          <span>Cotizador de Presupuestos</span>
        </div>
        <Link to="/" className="cotizador-back-btn">
          ← Volver al sitio
        </Link>
      </div>

      <div className="cotizador-layout">

        {/* FORMULARIO */}
        <div className="cotizador-form-panel">
          <h2>Datos del presupuesto</h2>

          {/* Número y fecha */}
          <div className="cotizador-row">
            <div className="cotizador-field">
              <label>N° Presupuesto</label>
              <input
                type="number"
                inputMode="numeric"
                {...register('numPresupuesto', { required: 'Requerido', min: { value: 1, message: 'Debe ser mayor a 0' } })}
              />
              {errors.numPresupuesto && <span className="error-msg">{errors.numPresupuesto.message}</span>}
            </div>
            <div className="cotizador-field">
              <label>Fecha</label>
              <input type="date" {...register('fecha', { required: 'Requerido' })} />
              {errors.fecha && <span className="error-msg">{errors.fecha.message}</span>}
            </div>
          </div>

          <div className="cotizador-section-title">Datos del cliente</div>

          <div className="cotizador-row full">
            <div className="cotizador-field">
              <label>Nombre y apellido *</label>
              <input
                type="text"
                placeholder="Ej: Juan Pérez"
                {...register('clienteNombre', { required: 'El nombre es obligatorio' })}
              />
              {errors.clienteNombre && <span className="error-msg">{errors.clienteNombre.message}</span>}
            </div>
          </div>

          <div className="cotizador-row">
            <div className="cotizador-field">
              <label>Teléfono</label>
              <input type="tel" placeholder="Ej: 264-4501234" {...register('clienteTelefono')} />
            </div>
            <div className="cotizador-field">
              <label>Email (opcional)</label>
              <input
                type="email"
                placeholder="cliente@mail.com"
                {...register('clienteEmail', {
                  pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Email inválido' },
                })}
              />
              {errors.clienteEmail && <span className="error-msg">{errors.clienteEmail.message}</span>}
            </div>
          </div>

          <div className="cotizador-section-title">Detalle del viaje</div>

          <div className="cotizador-row">
            <div className="cotizador-field">
              <label>Origen *</label>
              <input type="text" placeholder="Ej: San Juan" {...register('origen', { required: 'Requerido' })} />
              {errors.origen && <span className="error-msg">{errors.origen.message}</span>}
            </div>
            <div className="cotizador-field">
              <label>Destino *</label>
              <input type="text" placeholder="Ej: Mendoza" {...register('destino', { required: 'Requerido' })} />
              {errors.destino && <span className="error-msg">{errors.destino.message}</span>}
            </div>
          </div>

          <div className="cotizador-row">
            <div className="cotizador-field">
              <label>Fecha de salida</label>
              <input type="date" {...register('fechaSalida')} />
            </div>
            <div className="cotizador-field">
              <label>Fecha de regreso (opcional)</label>
              <input type="date" {...register('fechaRegreso')} />
            </div>
          </div>

          <div className="cotizador-row">
            <div className="cotizador-field">
              <label>Cantidad de pasajeros</label>
              <input
                type="number"
                inputMode="numeric"
                min="1"
                placeholder="Ej: 10"
                {...register('cantPasajeros', { min: { value: 1, message: 'Mínimo 1' } })}
              />
              {errors.cantPasajeros && <span className="error-msg">{errors.cantPasajeros.message}</span>}
            </div>
            <div className="cotizador-field">
              <label>Tipo de servicio</label>
              <select {...register('tipoServicio')}>
                {TIPOS_SERVICIO.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="cotizador-row full">
            <div className="cotizador-field">
              <label>Descripción del servicio</label>
              <textarea
                placeholder="Describí el servicio, condiciones, incluye..."
                {...register('descripcion')}
              />
            </div>
          </div>

          <div className="cotizador-section-title">Importes</div>

          <div className="cotizador-row">
            <div className="cotizador-field">
              <label>Precio base ($)</label>
              <input
                type="number"
                inputMode="decimal"
                min="0"
                step="0.01"
                placeholder="0.00"
                {...register('precioBase', { min: { value: 0, message: 'No puede ser negativo' } })}
              />
              {errors.precioBase && <span className="error-msg">{errors.precioBase.message}</span>}
            </div>
            <div className="cotizador-field">
              <label>Adicionales ($)</label>
              <input
                type="number"
                inputMode="decimal"
                min="0"
                step="0.01"
                placeholder="0.00"
                {...register('adicionales', { min: { value: 0, message: 'No puede ser negativo' } })}
              />
            </div>
          </div>

          <div className="cotizador-row">
            <div className="cotizador-field">
              <label>Descuento ($) (opcional)</label>
              <input
                type="number"
                inputMode="decimal"
                min="0"
                step="0.01"
                placeholder="0.00"
                {...register('descuento', { min: { value: 0, message: 'No puede ser negativo' } })}
              />
            </div>
            <div className="cotizador-field" />
          </div>

          <div className="cotizador-total-box">
            <span>Total calculado</span>
            <span>{formatMoney(total)}</span>
          </div>

          <div className="cotizador-section-title">Más información</div>

          <div className="cotizador-row full">
            <div className="cotizador-field">
              <label>Observaciones</label>
              <textarea
                placeholder="Notas adicionales, condiciones, forma de pago..."
                {...register('observaciones')}
              />
            </div>
          </div>

          <div className="cotizador-row full">
            <div className="cotizador-field">
              <label>Validez del presupuesto</label>
              <input
                type="text"
                placeholder="Ej: 30 días desde la fecha de emisión"
                {...register('validezPresupuesto')}
              />
            </div>
          </div>

          {/* ACCIONES */}
          <div className="cotizador-actions">
            <button
              type="button"
              className="cotizador-btn cotizador-btn-primary"
              onClick={handleDownloadPdf}
              disabled={loadingPdf}
            >
              {loadingPdf ? '⏳ Generando PDF...' : '📥 Descargar PDF'}
            </button>
            <button
              type="button"
              className="cotizador-btn cotizador-btn-secondary"
              onClick={handleSave}
            >
              💾 Guardar en este navegador
            </button>
            <button
              type="button"
              className="cotizador-btn cotizador-btn-outline"
              onClick={handleClear}
            >
              🗑 Limpiar formulario
            </button>
          </div>
        </div>

        {/* VISTA PREVIA */}
        <div className="cotizador-preview-panel">
          <h2>Vista previa del presupuesto</h2>
          <div className="cotizador-preview-viewport">
            <div className="cotizador-preview-scale">
              <CotizadorPreview ref={previewRef} data={{ ...values, total }} mode="screen" />
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
